import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { http } from "@/lib/api";

/**
 * Navbar notification bell — polls /api/notifications, shows unread badge,
 * dropdown lists last 10, click marks-read + navigates.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await http.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // not logged-in or transient — keep silent
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // every 60s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await http.post(`/notifications/${id}/read`);
      setItems((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* noop */ }
  };

  const markAll = async () => {
    try {
      await http.post("/notifications/read-all");
      setItems((p) => p.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* noop */ }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative text-white/70 hover:text-white p-2 rounded-md transition-colors"
        data-testid="notif-bell"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-electric text-black text-[10px] font-bold leading-4 text-center"
            data-testid="notif-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 md:w-96 max-h-[28rem] overflow-auto bento-card p-0 z-[60]"
          data-testid="notif-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="label-mono">Notifications</div>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAll}
                className="text-[10px] uppercase tracking-wider text-electric hover:text-white flex items-center gap-1"
                data-testid="notif-mark-all"
              >
                <CheckCheck size={11} /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-xs text-white/40" data-testid="notif-empty">
              No notifications yet.
            </div>
          ) : (
            <div>
              {items.map((n) => (
                <Link
                  key={n.id}
                  to={n.link || "#"}
                  onClick={() => { markRead(n.id); setOpen(false); }}
                  className={`block px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors ${n.read ? "" : "bg-electric/[0.04]"}`}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-electric flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-display font-bold uppercase tracking-wider text-white">{n.title}</div>
                      <div className="text-xs text-white/60 mt-0.5 leading-relaxed">{n.body}</div>
                      <div className="text-[10px] text-white/30 font-mono mt-1">
                        {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
